import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link } from 'react-router-dom';
import { 
  Search, Home, Plus, Phone, Mail, Trash2, Loader2, X, 
  MapPin, BedDouble, Euro, CheckCircle, Upload, AlertCircle, Maximize2 
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import SEO from '../components/SEO';
import LoadingScreen from '../components/LoadingScreen';

// COSTANTI DI SICUREZZA
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_TITLE_LENGTH = 60;
const MAX_DESC_LENGTH = 1000;
const MAX_ZONE_LENGTH = 50;
const ADMIN_EMAIL = 'info@tusciahub.it'; 

const Housing = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Filtri
  const [selectedType, setSelectedType] = useState('Tutti');
  const [searchZone, setSearchZone] = useState('');
  const types = ['Tutti', 'Singola', 'Doppia', 'Monolocale', 'Appartamento', 'Cerco Coinquilino'];

  // Stati Modali
  const [isSellOpen, setIsSellOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState(false);
  
  const [toast, setToast] = useState(null);

  const [newItem, setNewItem] = useState({ 
    title: '', description: '', price: '', type: 'Singola', 
    zone: '', contact_info: '', contact_type: 'whatsapp', 
    image: null, termsAccepted: false 
  });

  useEffect(() => {
    const init = async () => {
        setLoading(true);
        await checkUser();
        await fetchItems();
        setLoading(false);
    };
    init();
  }, []);

  // --- FIX SCROLL: BLOCCA SCROLL PAGINA QUANDO MODALE APERTO ---
  useEffect(() => {
    if (isSellOpen || selectedItem) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => { 
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isSellOpen, selectedItem]);

  const checkUser = async () => { 
      const { data: { user } } = await supabase.auth.getUser(); 
      setUser(user); 
      if (user && user.email === ADMIN_EMAIL) {
          setIsAdmin(true);
      }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('housing_items')
        .select('*, profiles(email)')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setItems(data || []);
    } catch (error) { 
      console.error(error); 
    }
  };

  const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
          showToast("Carica solo immagini (JPG, PNG).", "error");
          e.target.value = null;
          return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
          setFileError(true);
          setNewItem({ ...newItem, image: null });
          e.target.value = null;
      } else {
          setFileError(false);
          setNewItem({ ...newItem, image: file });
      }
  };

  const handleAddItem = async (e) => {
    e.preventDefault(); 
    if (!user) return showToast("Devi accedere per pubblicare!", "error");
    if (!newItem.termsAccepted) return showToast("Devi accettare i termini.", "error");
    if (!newItem.image) return showToast("Carica una foto obbligatoria!", "error"); 
    if (fileError) return showToast("File troppo grande.", "error");

    if (newItem.title.length > MAX_TITLE_LENGTH) return showToast("Titolo troppo lungo.", "error");
    if (newItem.description.length > MAX_DESC_LENGTH) return showToast("Descrizione troppo lunga.", "error");
    if (newItem.zone.length > MAX_ZONE_LENGTH) return showToast("Nome zona troppo lungo.", "error");
    
    setIsSubmitting(true);
    
    try {
      const fileExt = newItem.image.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}_img.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('housing_images')
        .upload(fileName, newItem.image);
      
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('housing_images')
        .getPublicUrl(fileName);
      
      const { error: dbError } = await supabase.from('housing_items').insert([{ 
        title: newItem.title, description: newItem.description, 
        price: parseFloat(newItem.price), type: newItem.type, 
        zone: newItem.zone, contact_info: newItem.contact_info, 
        contact_type: newItem.contact_type, image_url: publicUrl, 
        user_id: user.id 
      }]);

      if (dbError) throw dbError;
      
      showToast("Annuncio pubblicato!", "success");
      setIsSellOpen(false); 
      setNewItem({ title: '', description: '', price: '', type: 'Singola', zone: '', contact_info: '', contact_type: 'whatsapp', image: null, termsAccepted: false });
      setFileError(false);
      await fetchItems();

    } catch (error) { showToast("Errore: " + error.message, "error"); } 
    finally { setIsSubmitting(false); }
  };

  const handleDelete = async (itemId, imageUrl) => { 
      if(!window.confirm("Sicuro di voler eliminare questo annuncio?")) return; 
      
      try { 
          if (imageUrl) {
              const fileUrlParts = imageUrl.split('/housing_images/');
              if (fileUrlParts.length > 1) {
                  const filePath = fileUrlParts[1];
                  const { error: storageError } = await supabase.storage
                      .from('housing_images') 
                      .remove([filePath]);
                  if (storageError) console.error("Errore cancellazione file:", storageError);
              }
          }
          const { error } = await supabase.from('housing_items').delete().eq('id', itemId);
          if (error) throw error;

          setItems(items.filter(i => i.id !== itemId)); 
          showToast("Eliminato.", "success");
          if(selectedItem?.id === itemId) setSelectedItem(null); 

      } catch(err) { showToast("Errore cancellazione.", "error"); } 
  };

  const filteredItems = items.filter(item => {
    const matchesType = selectedType === 'Tutti' || item.type === selectedType;
    const matchesZone = searchZone === '' || (item.zone && item.zone.toLowerCase().includes(searchZone.toLowerCase()));
    return matchesType && matchesZone;
  });

  const inputStyle = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-sm font-medium text-slate-700 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-green-200 focus:border-green-300 outline-none transition-all shadow-sm";

  if (loading) return <LoadingScreen />;

  return (
    <div className="relative min-h-screen bg-slate-100 pt-24 pb-20 px-6 font-sans text-slate-800">
      <SEO title="Cerca Casa" description="Affitti studenti." url="/affitti" />
      <div className="absolute top-0 left-[-10%] w-[500px] h-[500px] bg-green-200/40 rounded-full blur-[100px] mix-blend-multiply pointer-events-none"></div>
      
      {toast && (
        <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300 px-6 py-4 rounded-xl shadow-2xl flex items-center gap-4 border min-w-[320px] ${toast.type === 'error' ? 'bg-red-900 text-white border-red-800' : 'bg-slate-900 text-white border-slate-800'}`}>
            {toast.type === 'error' ? <AlertCircle className="text-red-400"/> : <CheckCircle className="text-green-400"/>}
            <span className="font-bold text-sm">{toast.message}</span>
        </div>
      )}

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar { height: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto">
        <PageHeader title="Cerca Casa" subtitle="Stanze e appartamenti per studenti." breadcrumbs={[{label: 'Affitti'}]} />

        {/* --- BARRA FILTRI --- */}
        <div className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm mb-10">
            <div className="flex flex-wrap xl:flex-nowrap gap-6 items-center justify-between">
                
                <div className="flex flex-col md:flex-row gap-6 w-full xl:w-auto flex-grow min-w-0">
                    <div className="flex-1 w-full md:w-auto overflow-x-auto pb-4 custom-scrollbar min-w-0">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">Tipologia</label>
                        <div className="flex gap-2">
                            {types.map(t => (
                                <button key={t} onClick={() => setSelectedType(t)} className={`px-5 py-2.5 rounded-xl text-sm font-bold whitespace-nowrap transition border ${selectedType === t ? 'bg-green-600 text-white border-green-600 shadow-md' : 'bg-white border-slate-200 text-slate-500 hover:border-green-200 hover:text-green-600'}`}>{t}</button>
                            ))}
                        </div>
                    </div>
                    
                    <div className="w-full md:w-[280px] shrink-0">
                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 block ml-1">Cerca Zona</label>
                        <div className="relative group">
                            <input type="text" placeholder="Es. Centro..." className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2.5 pl-10 pr-4 font-bold outline-none focus:ring-2 focus:ring-green-400 focus:bg-white transition-all text-sm group-hover:border-slate-300" value={searchZone} onChange={e => setSearchZone(e.target.value)}/>
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18}/>
                        </div>
                    </div>
                </div>

                <button 
                    onClick={() => user ? setIsSellOpen(true) : showToast("Accedi per pubblicare!", "error")} 
                    className="w-full xl:w-auto bg-green-500 hover:bg-green-600 text-white px-8 py-3.5 rounded-xl font-bold shadow-lg shadow-green-200 flex items-center justify-center gap-2 transition-transform hover:-translate-y-0.5 active:scale-95 shrink-0 mt-2 xl:mt-0"
                >
                    <Plus size={20}/> Pubblica
                </button>
            </div>
        </div>

        {/* --- GRIGLIA ANNUNCI --- */}
        {filteredItems.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredItems.map(item => (
                    // CARD STYLE: Sfondo sfumato, bordo colorato, ombra forte (come Partner/Jobs)
                    <div key={item.id} onClick={() => setSelectedItem(item)} className="group bg-gradient-to-b from-white to-green-50 rounded-[2.5rem] border-2 border-green-100 shadow-xl hover:shadow-2xl hover:border-green-300 hover:-translate-y-2 transition-all duration-300 cursor-pointer overflow-hidden flex flex-col h-full relative">
                        
                        {/* Immagine */}
                        <div className="h-64 bg-slate-100 relative overflow-hidden">
                            <img src={item.image_url} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" alt={item.title}/>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60"></div>
                            
                            {/* Badge Tipo */}
                            <span className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-md text-slate-900 text-[10px] font-black px-3 py-1.5 rounded-xl shadow-lg flex items-center gap-1">
                                <BedDouble size={12}/> {item.type}
                            </span>
                            
                            {/* Overlay Hover */}
                            <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="bg-white/90 text-slate-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                                    <Maximize2 size={16}/> Vedi Dettagli
                                </span>
                            </div>
                        </div>

                        {/* Corpo Card */}
                        <div className="p-6 flex flex-col flex-grow">
                            <div className="flex items-center gap-2 mb-3 text-xs font-bold text-slate-400">
                                <span className="bg-white text-green-700 px-2 py-1 rounded-lg border border-green-100 flex items-center gap-1 truncate max-w-[200px]">
                                    <MapPin size={12}/> {item.zone}
                                </span>
                                <span>•</span>
                                <span>{new Date(item.created_at).toLocaleDateString()}</span>
                            </div>
                            
                            <h3 className="font-black text-slate-800 text-xl leading-tight mb-4 line-clamp-1 group-hover:text-green-600 transition-colors">
                                {item.title}
                            </h3>

                            {/* BOX PREZZO VIBRANTE (Stile Jobs/Partner) */}
                            <div className="mb-5">
                                <div className="w-full bg-gradient-to-r from-green-500 to-emerald-600 p-4 rounded-2xl text-white shadow-lg shadow-green-200/50 flex flex-col items-center justify-center relative overflow-hidden group-hover:scale-[1.02] transition-transform duration-300">
                                    <p className="text-[10px] font-bold opacity-90 uppercase tracking-widest mb-1 text-white/90">Prezzo Mensile</p>
                                    <div className="flex items-center gap-1">
                                        <Euro size={18} strokeWidth={3} className="text-white opacity-80"/>
                                        <span className="text-2xl font-black tracking-tight text-white">{item.price}</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Descrizione e Footer */}
                            <p className="text-slate-500 text-sm line-clamp-2 mb-4 leading-relaxed">{item.description}</p>
                            
                            <div className="mt-auto pt-4 border-t border-green-100 flex justify-between items-center">
                                <span className="text-sm font-bold text-green-600 group-hover:underline">Apri annuncio &rarr;</span>
                                
                                {user && (user.id === item.user_id || isAdmin) && (
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleDelete(item.id, item.image_url); }} 
                                        className="bg-white text-red-500 p-2.5 rounded-xl hover:bg-red-50 transition z-20 border border-red-100 shadow-sm"
                                        title="Elimina Annuncio"
                                    >
                                        <Trash2 size={18}/>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        ) : (
            <div className="text-center py-24 bg-white border-2 border-dashed border-slate-200 rounded-[3rem]">
                <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 text-green-400 animate-pulse"><Home size={32}/></div>
                <h3 className="font-bold text-slate-900 text-lg">Nessun annuncio trovato</h3>
                <p className="text-slate-400 text-sm mb-6">Prova a cambiare i filtri o pubblica tu il primo annuncio!</p>
                <button onClick={() => user ? setIsSellOpen(true) : showToast("Accedi prima!", "error")} className="text-green-600 font-bold bg-white border border-green-200 px-6 py-2 rounded-full hover:bg-green-50 transition shadow-sm">Pubblica Annuncio</button>
            </div>
        )}

        {/* --- MODALE DETTAGLIO: FIX MOBILE (Padding, Scroll Bloccato, Header Flex) --- */}
        {selectedItem && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setSelectedItem(null)}></div>
                <div className="relative z-10 bg-white w-full max-w-4xl max-h-[90vh] rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col md:flex-row animate-in zoom-in-95 duration-300">
                    
                    {/* Immagine Modale */}
                    <div className="w-full md:w-1/2 h-64 md:h-auto bg-slate-100 relative shrink-0">
                        <img src={selectedItem.image_url} className="w-full h-full object-cover" alt={selectedItem.title}/>
                        <button onClick={() => setSelectedItem(null)} className="absolute top-4 left-4 md:hidden p-2 bg-white/50 backdrop-blur rounded-full hover:bg-white text-slate-800 transition shadow-sm"><X size={20}/></button>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6 pt-20">
                            <span className="bg-white text-slate-900 px-3 py-1 rounded-lg text-xs font-black uppercase tracking-widest">{selectedItem.type}</span>
                        </div>
                    </div>

                    {/* Contenuto Modale */}
                    <div className="w-full md:w-1/2 p-8 overflow-y-auto bg-white flex flex-col custom-scrollbar relative">
                        {/* Tasto Chiudi Desktop */}
                        <button onClick={() => setSelectedItem(null)} className="absolute top-6 right-6 hidden md:block p-2 bg-slate-50 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"><X size={20}/></button>

                        <div className="flex items-center gap-2 text-slate-400 text-sm font-bold mb-2 pt-2 md:pt-0">
                            <MapPin size={16} className="text-green-500"/> {selectedItem.zone}
                            <span>•</span> {new Date(selectedItem.created_at).toLocaleDateString()}
                        </div>
                        
                        <h2 className="text-3xl font-black text-slate-900 leading-tight mb-2">{selectedItem.title}</h2>
                        
                        <div className="text-2xl font-black text-green-600 mb-6 flex items-center gap-1">
                            {selectedItem.price} <span className="text-sm text-slate-400 font-medium">€/mese</span>
                        </div>
                        
                        <div className="prose prose-slate prose-sm text-slate-600 mb-8 leading-relaxed">
                            <p className="whitespace-pre-wrap">{selectedItem.description}</p>
                        </div>
                        
                        <div className="mt-auto space-y-3 pb-8 md:pb-0">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Contatta l'inserzionista</p>
                            <a 
                                href={selectedItem.contact_type === 'whatsapp' ? `https://wa.me/${selectedItem.contact_info}` : `mailto:${selectedItem.contact_info}`} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className={`w-full text-white py-4 rounded-xl text-lg font-bold flex items-center justify-center gap-2 transition shadow-xl hover:-translate-y-1 active:scale-95 ${selectedItem.contact_type === 'whatsapp' ? 'bg-green-500 hover:bg-green-600 shadow-green-200' : 'bg-blue-500 hover:bg-blue-600 shadow-blue-200'}`}
                            >
                                {selectedItem.contact_type === 'whatsapp' ? <Phone size={24}/> : <Mail size={24}/>} 
                                {selectedItem.contact_type === 'whatsapp' ? 'Scrivigli su WhatsApp' : 'Invia una Email'}
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* --- MODALE UPLOAD: FIX MOBILE (P-4) + LAYOUT ORIGINALE --- */}
        {isSellOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
                <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setIsSellOpen(false)}></div>
                <div className="relative z-10 bg-white w-full max-h-[90vh] md:max-w-lg rounded-[2.5rem] shadow-2xl flex flex-col animate-in zoom-in-95 duration-200 p-8 border border-white/20">
                    
                    <button onClick={() => setIsSellOpen(false)} className="absolute top-6 right-6 p-2 bg-slate-50 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition"><X size={20}/></button>
                    
                    <div className="mb-6 pr-10">
                        <h3 className="text-2xl font-black text-slate-900 mb-1">Pubblica Annuncio</h3>
                        <p className="text-slate-500 text-sm font-medium">Compila i dettagli per trovare inquilini.</p>
                    </div>

                    <div className="overflow-y-auto custom-scrollbar flex-1 -mr-4 pr-4">
                        <form onSubmit={handleAddItem} className="space-y-4 pb-2">
                            <div className="space-y-1"><input type="text" required maxLength={MAX_TITLE_LENGTH} className={inputStyle} placeholder="Titolo annuncio" value={newItem.title} onChange={e => setNewItem({...newItem, title: e.target.value})} /></div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div className="relative">
                                    <input type="number" required min="0" className={inputStyle} placeholder="Prezzo" value={newItem.price} onChange={e => setNewItem({...newItem, price: e.target.value})} />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs font-bold">€/mese</span>
                                </div>
                                <select className={`${inputStyle} cursor-pointer appearance-none`} value={newItem.type} onChange={e => setNewItem({...newItem, type: e.target.value})}>
                                    {types.filter(t => t !== 'Tutti').map(t => <option key={t}>{t}</option>)}
                                </select>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <input type="text" required maxLength={MAX_ZONE_LENGTH} className={inputStyle} placeholder="Zona (es. Centro)" value={newItem.zone} onChange={e => setNewItem({...newItem, zone: e.target.value})} />
                                <div className="flex bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
                                    <select className="bg-slate-100 text-xs font-bold px-2 py-3 border-r border-slate-200 outline-none text-slate-600 cursor-pointer" value={newItem.contact_type} onChange={e => setNewItem({...newItem, contact_type: e.target.value})}>
                                        <option value="whatsapp">WA</option>
                                        <option value="email">Mail</option>
                                    </select>
                                    <input type="text" required className="w-full bg-transparent px-3 text-sm font-medium outline-none placeholder:text-slate-400" placeholder="Contatto" value={newItem.contact_info} onChange={e => setNewItem({...newItem, contact_info: e.target.value})} />
                                </div>
                            </div>
                            
                            <textarea className={`${inputStyle} h-28 resize-none`} placeholder="Descrizione dettagliata..." maxLength={MAX_DESC_LENGTH} value={newItem.description} onChange={e => setNewItem({...newItem, description: e.target.value})}></textarea>
                            
                            <div className="relative group">
                                <input type="file" required accept="image/*" className="hidden" id="img-upload" onChange={handleFileChange} />
                                <label htmlFor="img-upload" className={`flex flex-col items-center justify-center h-32 w-full border-2 border-dashed rounded-2xl transition-all group-hover:scale-[1.01] cursor-pointer ${fileError ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-green-300'}`}>
                                    {newItem.image ? (
                                        <div className="flex flex-col items-center text-green-600 animate-in zoom-in">
                                            <CheckCircle size={32} className="mb-1"/>
                                            <span className="text-xs font-bold">{newItem.image.name}</span>
                                        </div>
                                    ) : (
                                        <div className="flex flex-col items-center text-slate-400 group-hover:text-green-500 transition-colors">
                                            <div className="bg-white p-3 rounded-full shadow-sm mb-2"><Upload size={20}/></div>
                                            <span className="text-xs font-bold">{fileError ? "File troppo grande" : "Carica Foto Copertina"}</span>
                                        </div>
                                    )}
                                </label>
                            </div>

                            <label className="flex items-center gap-3 p-2 cursor-pointer hover:bg-slate-50 rounded-lg transition">
                                <input type="checkbox" required className="w-4 h-4 rounded text-green-600 focus:ring-green-500 cursor-pointer" checked={newItem.termsAccepted} onChange={e => setNewItem({...newItem, termsAccepted: e.target.checked})}/>
                                <span className="text-xs text-slate-500 font-medium">Accetto i <Link to="/terms" className="text-green-600 font-bold hover:underline" target="_blank">Termini e Condizioni</Link>.</span>
                            </label>

                            <button disabled={isSubmitting} type="submit" className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold text-lg hover:bg-green-600 transition shadow-xl shadow-slate-200 active:scale-95 flex items-center justify-center gap-2">
                                {isSubmitting ? <Loader2 className="animate-spin"/> : 'Pubblica Annuncio'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Housing;